import { getIncomeById, OnboardingDetailsRole } from '@nemuru-eng/js-api-client';

import { LoanPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/entities/PaymentMethodDetail/LoanPaymentMethodDetailEntity';
import { StoredBorrowerOnboardingDetails } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/types';
import { BorrowerOnboardingDetailsMap } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPosForm/components/BorrowerOnboardingDetailsForm/maps/borrowerOnboardingDetails.map';
import { useQuery } from '@/hooks/useQuery';

export const useGetOnboardingDetailsQuery = (paymentMethodDetailEntity: LoanPaymentMethodDetailEntity) => {
  const getOnboardingDetails = async (): Promise<StoredBorrowerOnboardingDetails[] | null> => {
    const loan = paymentMethodDetailEntity.loan;

    if (!loan) {
      return null;
    }

    const storedOnboardingDetails: StoredBorrowerOnboardingDetails[] = [];

    // Borrower
    const borrowerOnboardingDetails = paymentMethodDetailEntity.borrowerOnboardingDetails();

    if (!borrowerOnboardingDetails || !borrowerOnboardingDetails.userDetails) {
      return null;
    }

    const borrowerIncome = await getIncomeById({
      loanId: loan.id,
      onboardingDetailsId: borrowerOnboardingDetails.id,
    });

    const borrowerOnboardingDetailsForm = BorrowerOnboardingDetailsMap.fromLoanOnboardingDetailsToForm(
      borrowerOnboardingDetails,
      borrowerIncome,
      loan.borrowerCashInCashOutMethodAccountNumber,
    )!;

    storedOnboardingDetails.push(
      BorrowerOnboardingDetailsMap.fromFormToStored(
        borrowerOnboardingDetailsForm,
        OnboardingDetailsRole.BORROWER,
        loan.loanConditions.currency,
      ),
    );

    // Co-borrower
    const coBorrowerOnboardingDetails = paymentMethodDetailEntity.coBorrowerOnboardingDetails();

    if (coBorrowerOnboardingDetails && coBorrowerOnboardingDetails.userDetails) {
      const coBorrowerIncome = await getIncomeById({
        loanId: loan.id,
        onboardingDetailsId: coBorrowerOnboardingDetails.id,
      });

      const coBorrowerOnboardingDetailsForm = BorrowerOnboardingDetailsMap.fromLoanOnboardingDetailsToForm(
        coBorrowerOnboardingDetails,
        coBorrowerIncome,
        loan.borrowerCashInCashOutMethodAccountNumber,
      )!;

      storedOnboardingDetails.push(
        BorrowerOnboardingDetailsMap.fromFormToStored(
          coBorrowerOnboardingDetailsForm,
          OnboardingDetailsRole.CO_BORROWER,
          loan.loanConditions.currency,
        ),
      );
    }

    // Guarantor
    const guarantorOnboardingDetails = paymentMethodDetailEntity.guarantorOnboardingDetails();

    if (guarantorOnboardingDetails && guarantorOnboardingDetails.userDetails) {
      const guarantorIncome = await getIncomeById({
        loanId: loan.id,
        onboardingDetailsId: guarantorOnboardingDetails.id,
      });

      const guarantorOnboardingDetailsForm = BorrowerOnboardingDetailsMap.fromLoanOnboardingDetailsToForm(
        guarantorOnboardingDetails,
        guarantorIncome,
        loan.borrowerCashInCashOutMethodAccountNumber,
      )!;

      storedOnboardingDetails.push(
        BorrowerOnboardingDetailsMap.fromFormToStored(
          guarantorOnboardingDetailsForm,
          OnboardingDetailsRole.GUARANTOR,
          loan.loanConditions.currency,
        ),
      );
    }

    return storedOnboardingDetails;
  };

  return useQuery({
    queryFn: getOnboardingDetails,
  });
};
